import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, Modal } from '@/components/ui';
import { runLifecycleCommand, type LifecycleCommand } from '@/api/services/employment.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { usePermissions } from '@/hooks/usePermissions';
import { ExitTypeSelect } from '@/components/common/ExitTypeSelect';
import type { ExitType } from '@/lib/exitTypes';

interface CommandSpec {
  command: LifecycleCommand;
  label: string;
  needsReason?: boolean;
  needsLastWorkingDate?: boolean;
  /** Ends the employment: an exit type is required, a reason optional. */
  needsExitType?: boolean;
}

/**
 * Commands offered for the employee's current lifecycle status. The server's
 * transition table stays authoritative; this only avoids offering a command
 * that is certain to be rejected.
 */
const COMMANDS_BY_STATUS: Record<string, CommandSpec[]> = {
  preboarding: [{ command: 'start-onboarding', label: 'Start onboarding' }],
  onboarding: [{ command: 'start-probation', label: 'Start probation' }],
  probation: [
    { command: 'confirm-employment', label: 'Confirm employment' },
    { command: 'suspend', label: 'Suspend', needsReason: true },
    {
      command: 'begin-separation-notice',
      label: 'Begin separation notice',
      needsReason: true,
      needsLastWorkingDate: true,
    },
  ],
  active: [
    { command: 'suspend', label: 'Suspend', needsReason: true },
    {
      command: 'begin-separation-notice',
      label: 'Begin separation notice',
      needsReason: true,
      needsLastWorkingDate: true,
    },
  ],
  on_leave: [
    { command: 'return-from-leave', label: 'Return from leave' },
    { command: 'suspend', label: 'Suspend', needsReason: true },
  ],
  suspended: [
    { command: 'reinstate', label: 'Reinstate', needsReason: true },
    {
      command: 'begin-separation-notice',
      label: 'Begin separation notice',
      needsReason: true,
      needsLastWorkingDate: true,
    },
  ],
  notice: [
    { command: 'cancel-separation', label: 'Cancel separation', needsReason: true },
    { command: 'complete-company-exit', label: 'Complete company exit', needsExitType: true },
  ],
  exited: [{ command: 'archive-employment', label: 'Archive employment' }],
};

/** Separation and exit also require `hr:offboarding`; the server refuses them otherwise. */
const OFFBOARDING_COMMANDS: LifecycleCommand[] = [
  'begin-separation-notice',
  'cancel-separation',
  'complete-company-exit',
  'archive-employment',
];

/** Decided on the probation case while one is undecided; the server refuses them here. */
const PROBATION_CASE_COMMANDS: LifecycleCommand[] = [
  'confirm-employment',
  'begin-separation-notice',
];

export function EmployeeLifecycleActions({
  companyId,
  userId,
  lifecycleStatus,
  probationCaseOpen = false,
}: {
  companyId: string;
  userId: string;
  lifecycleStatus: string;
  /** True while an undecided probation case governs confirmation and ending. */
  probationCaseOpen?: boolean;
}): React.ReactElement | null {
  const [active, setActive] = useState<CommandSpec | null>(null);
  const [reason, setReason] = useState('');
  const [lastWorkingDate, setLastWorkingDate] = useState('');
  const [exitType, setExitType] = useState<ExitType | ''>('');
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canOffboard = can('hr', 'offboarding');
  const governedByCase = probationCaseOpen && lifecycleStatus === 'probation';
  const available = (COMMANDS_BY_STATUS[lifecycleStatus] ?? []).filter(
    (spec) =>
      (!governedByCase || !PROBATION_CASE_COMMANDS.includes(spec.command)) &&
      (canOffboard || !OFFBOARDING_COMMANDS.includes(spec.command))
  );

  const mutation = useMutation({
    mutationFn: (spec: CommandSpec) =>
      runLifecycleCommand(companyId, userId, spec.command, {
        requestId: crypto.randomUUID(),
        ...(spec.needsReason && { reason }),
        ...(spec.needsLastWorkingDate && {
          lastWorkingDate: new Date(lastWorkingDate).toISOString(),
        }),
        ...(spec.needsExitType && exitType && { exitType }),
        ...(spec.needsExitType && reason.trim() && { reason: reason.trim() }),
      }),
    onSuccess: (result) => {
      toast.success(`Employment is now ${result.lifecycleStatus.replaceAll('_', ' ')}`);
      void queryClient.invalidateQueries({ queryKey: ['hr-employee-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['hr-employee-timeline'] });
      void queryClient.invalidateQueries({ queryKey: ['hr-employments'] });
      closeDialog();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const closeDialog = (): void => {
    setActive(null);
    setReason('');
    setLastWorkingDate('');
    setExitType('');
  };

  if (available.length === 0 && !governedByCase) return null;

  const canSubmit =
    active !== null &&
    (!active.needsReason || reason.trim().length >= 3) &&
    (!active.needsLastWorkingDate || lastWorkingDate !== '') &&
    (!active.needsExitType || exitType !== '') &&
    (!active.needsExitType || reason.trim() === '' || reason.trim().length >= 3);

  return (
    <>
      {governedByCase && (
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">
          Confirmation and ending are decided on the open probation case.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {available.map((spec) => (
          <Button
            key={spec.command}
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setActive(spec)}
          >
            {spec.label}
          </Button>
        ))}
      </div>
      <Modal open={active !== null} onClose={closeDialog} title={active?.label ?? ''}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This records an auditable lifecycle transition.
          </p>
          {active?.needsReason && (
            <Input
              id="lifecycle-reason"
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Recorded with the transition"
            />
          )}
          {active?.needsExitType && (
            <>
              <ExitTypeSelect id="lifecycle-exit-type" value={exitType} onChange={setExitType} />
              <Input
                id="lifecycle-exit-reason"
                label="Reason (optional)"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Recorded with the exit"
              />
            </>
          )}
          {active?.needsLastWorkingDate && (
            <Input
              id="lifecycle-last-working-date"
              label="Last working date"
              type="date"
              value={lastWorkingDate}
              onChange={(event) => setLastWorkingDate(event.target.value)}
            />
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canSubmit || mutation.isPending}
              loading={mutation.isPending}
              onClick={() => active && mutation.mutate(active)}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
